import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import InvestorLanding from '../pages/InvestorLanding';
import VendorLanding from '../pages/VendorLanding';
import VendorStorefront from '../pages/VendorStorefront';
import PropertyDetailPage from '../pages/PropertyDetailPage';
import SharedLinkGate from '../components/SharedLinkGate';
import SharedVendorPreview from '../pages/SharedVendorPreview';
import SharedPropertyPreview from '../pages/SharedPropertyPreview';
import App from '../App';
import Header from '../components/Header';

const normalizeUserType = (userProfile) => {
  const normalized = (userProfile?.userType || '').toLowerCase();
  if (normalized === 'wholesaler' || normalized === 'vendor') {
    return 'vendor';
  }
  if (normalized === 'investor') {
    return 'investor';
  }
  return null;
};

const getPreferredRoute = (userProfile) => {
  return normalizeUserType(userProfile) === 'vendor' ? '/vendor' : '/investor';
};

function AuthRedirectHandler() {
  const { currentUser, userProfile, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && currentUser && location.pathname === '/') {
      // Only redirect from the home page; allow manual navigation elsewhere
      navigate(getPreferredRoute(userProfile), { replace: true });
    }
  }, [currentUser, userProfile, loading, navigate, location.pathname]);

  return null;
}

function ScrollToTopOnRouteChange() {
  const location = useLocation();

  useEffect(() => {
    // If the URL has a hash, allow in-page anchors (e.g. /vendor#properties).
    if (location.hash) return;
    if (typeof window === 'undefined') return;

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.hash]);

  return null;
}

function ProtectedRoute({ children, allowedUserType }) {
  const { currentUser, userProfile, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#008080',
        color: 'white'
      }}>
        <div>
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  if (allowedUserType) {
    const targetType = allowedUserType.toLowerCase();
    const userType = normalizeUserType(userProfile);
    if (userType && userType !== targetType) {
      return <Navigate to={getPreferredRoute(userProfile)} replace />;
    }
  }

  return children;
}

function AppRouterContent() {
  const { currentUser, userProfile } = useAuth();
  const showVendorHeader = Boolean(currentUser && normalizeUserType(userProfile) === 'vendor');

  return (
    <>
      <AuthRedirectHandler />
      <ScrollToTopOnRouteChange />
      {showVendorHeader && <Header />}
      <Routes>
        {/* Public route - landing page */}
        <Route path="/" element={<App />} />

        {/* Protected routes - redirect based on user type */}
        <Route
          path="/investor"
          element={
            <ProtectedRoute allowedUserType="investor">
              <InvestorLanding />
            </ProtectedRoute>
          }
        />

        <Route
          path="/vendor"
          element={
            <ProtectedRoute allowedUserType="vendor">
              <VendorLanding />
            </ProtectedRoute>
          }
        />

        <Route
          path="/vendor/:id"
          element={
            <SharedLinkGate preview={<SharedVendorPreview />}>
              <VendorStorefront />
            </SharedLinkGate>
          }
        />

        <Route
          path="/property/:id"
          element={
            <SharedLinkGate preview={<SharedPropertyPreview />}>
              <PropertyDetailPage />
            </SharedLinkGate>
          }
        />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function AppRouter() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#008080',
        color: 'white'
      }}>
        <div>
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AppRouterContent />
    </Router>
  );
}
